import React from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import {
  AlertBox,
  PhoneInput,
  RadioChangeEvent,
  RadioGroup,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
// antd
import { Col, Row } from "antd";
// types
import { PhoneInputType } from "Types/common.types";
import { UserCreationInput } from "Types/user.types";
import { useCountriesList, useStatesList } from "Hooks/index";

interface UserDetailsProps {
  input: UserCreationInput;
  error: any;
  onTextInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRadioInputChange?: (event: RadioChangeEvent) => void;
  onPhoneInputChange?: (value: PhoneInputType, name: string) => void;
  onSelectInputChange?: (value: string | string[], name: string) => void;
}

const UserDetails: React.FC<UserDetailsProps> = ({
  input,
  error,
  onTextInputChange,
  onRadioInputChange,
  onPhoneInputChange,
  onSelectInputChange,
}) => {
  const { t } = useTranslation();

  const stateOption = useStatesList(input?.country);
  const countryOption = useCountriesList();

  return (
    <>
      {input.id ? null : (
        <div className="center header-title">{t("title.userDetails").toUpperCase()}</div>
      )}
      <Row gutter={[15, 15]} className="mt-50">
        <Col xs={24} sm={24} md={12}>
          <TextInput
            isRequired={true}
            label={t("label.fullName")}
            placeholder={t("txt.firstNameExample")}
            value={input.fullName}
            name="fullName"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onTextInputChange && onTextInputChange(event);
            }}
          />
          <AlertBox text={error.fullName} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <div className="required-div">
            <RadioGroup
              options={[
                { key: "nationalID", label: t("label.nationalID"), value: "NRIC" },
                { key: "passport", label: t("label.passport"), value: "PASSPORT" },
              ]}
              direction="horizontal"
              isRequired={true}
              name="idPassportType"
              value={input.idPassportType}
              onChange={(event: RadioChangeEvent) => {
                onRadioInputChange && onRadioInputChange(event);
              }}
            />
          </div>
          <TextInput
            placeholder={t("txt.IDExample")}
            value={input.idPassportNo}
            name="idPassportNo"
            onChange={onTextInputChange}
          />
          <AlertBox text={error.idPassportNo || error.idPassportType} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          {/* TODO: change exported type for PhoneInput at LLL shared component */}
          <PhoneInput
            isRequired={true}
            isError={!!error.mobileNo}
            label={t("label.mobileNo")}
            name="mobileNo"
            value={input.mobileNo}
            onChange={(value) => {
              onPhoneInputChange && onPhoneInputChange(value, "mobileNo");
            }}
          />
          <AlertBox
            text={error.mobileNo || error["mobileNo.dialCode"] || error["mobileNo.number"]}
          />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <TextInput
            isRequired={true}
            label={t("label.emailAddress").toUpperCase()}
            placeholder={t("txt.emailAddressExample")}
            value={input.email}
            name="email"
            onChange={onTextInputChange}
          />
          <AlertBox text={error.email} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <label className="inp-label inp-label--required">{t("label.homeAddress")}</label>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.street1Example")}
              value={input.street1}
              name="street1"
              onChange={onTextInputChange}
            />
            <AlertBox text={error.street1} />
          </div>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.street2Example")}
              value={input?.street2}
              name="street2"
              onChange={onTextInputChange}
            />
            <AlertBox text={error.street2} />
          </div>
          <Row gutter={[15, 15]}>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                isRequired={true}
                placeholder={t("txt.postcode")}
                value={input.postcode}
                name="postcode"
                onChange={onTextInputChange}
              />
              <AlertBox text={error.postcode} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                isRequired={true}
                placeholder={t("txt.city")}
                value={input.city}
                name="city"
                onChange={onTextInputChange}
              />
              <AlertBox text={error.city} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <SelectInput
                showSearch
                options={stateOption}
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                value={input.state}
                isReadonly={isNil(input.country)}
                name="state"
                placeholder={t("option.state")}
                onChange={(value: string | string[]) => {
                  onSelectInputChange && onSelectInputChange(value, "state");
                }}
              />
              <AlertBox text={error.state} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <SelectInput
                showSearch
                options={countryOption}
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                value={input.country}
                name="country"
                placeholder={t("option.country")}
                onChange={(value: string | string[]) => {
                  onSelectInputChange && onSelectInputChange(value, "country");
                }}
              />
              <AlertBox text={error.country} />
            </Col>
          </Row>
        </Col>

        <Col xs={24} sm={24} md={12}>
          <Col xs={24} sm={24} className="mb-7">
            <SelectInput
              options={[
                {
                  label: "Male",
                  value: "MALE",
                },
                {
                  label: "Female",
                  value: "FEMALE",
                },
              ]}
              value={input.gender}
              name="gender"
              placeholder={t("txt.gender")}
              onChange={(value: string | string[]) => {
                onSelectInputChange && onSelectInputChange(value, "gender");
              }}
              label={t("label.gender")}
              isRequired={true}
            />
            <AlertBox text={error.gender} />
          </Col>
        </Col>
      </Row>
    </>
  );
};

export default UserDetails;
